'use client';

import {
  Box,
  Button,
  Dialog,
  Flex,
  HStack,
  IconButton,
  Input,
  Portal,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Bot, Copy, KeyRound, Server, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CapabilityStatus } from '@/components/settings/CapabilityCatalog';
import { useAuth } from '@/app/auth-provider';
import { MCP_CLIENTS, type McpClientId } from '@/components/mcp/McpClientGuide';
import {
  IntegrationCard,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { AttuneSelect } from '@/components/shared/ui/AttuneSelect';

interface ApiKeySummary {
  id: string;
  label: string;
  status: 'active' | 'revoked';
  created_at: string | null;
  updated_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
}

interface CreatedApiKey {
  id: string;
  value: string;
  label: string;
  created_at: string | null;
  expires_at: string | null;
}

const ATTUNE_MCP_BASE = 'https://app.attunerank.com';
const EXPIRY_OPTIONS = [
  { value: '365', label: '1 year' },
  { value: '90', label: '90 days' },
  { value: '30', label: '30 days' },
];

interface McpClientSetup {
  steps: string[];
  snippetTitle?: string;
  snippet?: string;
}

function buildClientSetup(clientId: McpClientId, endpoint: string, apiKey: string): McpClientSetup {
  const key = apiKey || '<your-attune-key>';
  if (clientId === 'chatgpt') {
    return {
      steps: [
        'Enable developer mode in ChatGPT under Settings → Apps → Advanced Settings.',
        'Open Settings → Apps → Create and name the app Attune.',
        `Enter ${endpoint} as the MCP endpoint and choose OAuth authentication.`,
        'Select Scan Tools, complete Attune authorization, then select Create.',
        'Enable Attune in a chat and call workspace.guide before starting work.',
      ],
    };
  }
  if (clientId === 'claude-code') {
    return {
      steps: [
        'Create an Attune key below.',
        'Run the command below in a terminal.',
        'Run claude mcp list, then open /mcp in Claude Code to verify Attune is connected.',
        'Call workspace.guide before starting work.',
      ],
      snippetTitle: 'Claude Code command',
      snippet: `claude mcp add --transport http --scope user --header "Authorization: Bearer ${key}" attune ${endpoint}`,
    };
  }
  if (clientId === 'cursor') {
    return {
      steps: [
        'Create an Attune key below.',
        'Open Cursor Settings → Tools & MCP and add the configuration below to your user mcp.json.',
        'Enable Attune and confirm its tools appear.',
        'Call workspace.guide before starting work.',
      ],
      snippetTitle: 'Cursor mcp.json',
      snippet: JSON.stringify(
        {
          mcpServers: {
            attune: {
              url: endpoint,
              headers: { 'x-attune-api-key': key },
            },
          },
        },
        null,
        2
      ),
    };
  }
  if (clientId === 'openclaw') {
    return {
      steps: [
        'Create an Attune key below.',
        'Run the command below to save the Streamable HTTP server.',
        'Run openclaw mcp doctor attune --probe to verify the connection.',
        'Call workspace.guide before starting work.',
      ],
      snippetTitle: 'OpenClaw command',
      snippet: `openclaw mcp set attune '${JSON.stringify({
        url: endpoint,
        transport: 'streamable-http',
        headers: { Authorization: `Bearer ${key}` },
      })}'`,
    };
  }
  return {
    steps: [
      'Create an Attune key below.',
      'Add the configuration below to ~/.codex/config.toml.',
      'Restart Codex, then open /mcp to verify Attune is connected.',
      'Call workspace.guide before starting work.',
    ],
    snippetTitle: 'Codex config.toml',
    snippet: `[mcp_servers.attune]\nurl = "${endpoint}"\nhttp_headers = { "x-attune-api-key" = "${key}" }`,
  };
}

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
}

export function AttuneMcpIntegration({ clientId = 'codex' }: { clientId?: McpClientId }) {
  const { user } = useAuth();
  const selectedClient = MCP_CLIENTS.find(client => client.id === clientId) || MCP_CLIENTS[0];
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [label, setLabel] = useState(`${selectedClient.name} MCP`);
  const [expiryDays, setExpiryDays] = useState('365');
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const endpoint = `${ATTUNE_MCP_BASE}/api/attune/mcp`;

  const clientSetup = useMemo(
    () => buildClientSetup(clientId, endpoint, createdKey?.value || ''),
    [clientId, createdKey?.value, endpoint]
  );

  useEffect(() => {
    setLabel(`${selectedClient.name} MCP`);
  }, [selectedClient.name]);

  const copyText = useCallback(async (text: string, labelText: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${labelText} copied`);
    } catch {
      toast.error(`Failed to copy ${labelText.toLowerCase()}`);
    }
  }, []);

  const fetchKeys = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/attune/auth/keys', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to load MCP keys');
      }

      const data = (await res.json()) as { keys?: ApiKeySummary[] };
      setKeys(Array.isArray(data.keys) ? data.keys : []);
    } catch (error) {
      console.error('Failed to fetch Attune MCP keys:', error);
      toast.error('Failed to load Attune MCP keys');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    if (!user) return;

    setCreating(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/attune/auth/keys', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          label: label.trim() || 'Attune MCP key',
          expires_in_days: Number(expiryDays),
        }),
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(String(payload.error || 'Failed to create API key'));
      }

      const data = (await res.json()) as { key: CreatedApiKey };
      setCreatedKey(data.key);
      toast.success('New Attune MCP key created');
      await fetchKeys();
    } catch (error) {
      console.error('Failed to create Attune MCP key:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!user) return;
    if (
      !confirm(
        'Revoke this Attune MCP key? Existing MCP clients using it will stop working immediately.'
      )
    ) {
      return;
    }

    setRevokingId(keyId);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/attune/auth/keys/${encodeURIComponent(keyId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(String(payload.error || 'Failed to revoke key'));
      }

      toast.success('Attune MCP key revoked');
      await fetchKeys();
    } catch (error) {
      console.error('Failed to revoke key:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to revoke key');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <IntegrationCard
        icon={<Bot size={18} />}
        title={`Connect ${selectedClient.name}`}
        description={selectedClient.detail}
        statusLabel="Hosted"
        statusTone="neutral"
        meta={<Text as="span">User-scoped keys. {keys.length} issued.</Text>}
      >
        <VStack align="stretch" gap="0">
          <Box
            pb="4"
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor="ui.border.subtle"
          >
            <HStack justify="space-between" gap="3" mb="3" flexWrap="wrap">
              <Box>
                <Text fontSize="sm" fontWeight="600" color="ui.text.primary">
                  Set up {selectedClient.name}
                </Text>
              </Box>
              <Button
                variant="ghost"
                size="xs"
                color="ui.text.secondary"
                onClick={() => void copyText(endpoint, 'Endpoint')}
              >
                <Copy size={13} />
                Copy endpoint
              </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap="5" alignItems="start">
              <VStack as="ol" align="stretch" gap="2" m="0" pl="5">
                {clientSetup.steps.map((step, index) => (
                  <Text
                    as="li"
                    key={`${clientId}-${index}`}
                    fontSize="sm"
                    color="ui.text.secondary"
                  >
                    {step}
                  </Text>
                ))}
              </VStack>

              {clientSetup.snippet && clientSetup.snippetTitle ? (
                <Box minW="0">
                  <ConfigSnippet
                    title={clientSetup.snippetTitle}
                    snippet={clientSetup.snippet}
                    onCopy={() =>
                      copyText(clientSetup.snippet || '', `${selectedClient.name} setup`)
                    }
                  />
                </Box>
              ) : null}
            </SimpleGrid>
          </Box>

          {selectedClient.usesApiKey ? (
            <Box
              py="4"
              borderBottomWidth="1px"
              borderBottomStyle="solid"
              borderBottomColor="ui.border.subtle"
            >
              <HStack gap={2} mb="3">
                <KeyRound size={16} />
                <Text fontSize="sm" fontWeight="600" color="ui.text.primary">
                  Create a key for {selectedClient.name}
                </Text>
              </HStack>

              <HStack align="end" gap="3" flexWrap="wrap">
                <Box flex="1" minW={{ base: '100%', md: '240px' }}>
                  <Text fontSize="xs" color="ui.text.tertiary" mb="2">
                    Label
                  </Text>
                  <Input
                    aria-label="API key label"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    placeholder="Agent client"
                    bg="ui.surface.input"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="ui.border.subtle"
                    borderRadius="8px"
                    color="ui.text.primary"
                    _placeholder={{ color: 'ui.text.tertiary' }}
                    _hover={{ borderColor: 'ui.border.default' }}
                    _focus={{ borderColor: 'accent.solid', boxShadow: 'none' }}
                  />
                </Box>
                <Box minW="180px">
                  <Text fontSize="xs" color="ui.text.tertiary" mb="2">
                    Expiry
                  </Text>
                  <AttuneSelect
                    ariaLabel="API key expiry"
                    options={EXPIRY_OPTIONS}
                    value={expiryDays}
                    onValueChange={setExpiryDays}
                    size="md"
                    bg="ui.surface.input"
                    borderRadius="8px"
                    triggerProps={{
                      bg: 'ui.surface.input',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'ui.border.subtle',
                      borderRadius: '8px',
                      color: 'ui.text.primary',
                      _hover: { borderColor: 'ui.border.default' },
                    }}
                  />
                </Box>
                <Button
                  bg="accent.solid"
                  color="accent.contrast"
                  _hover={{ bg: 'accent.emphasized' }}
                  borderRadius="8px"
                  size="md"
                  onClick={handleCreate}
                  disabled={creating}
                >
                  {creating ? <InlineSpinner size="xs" /> : <KeyRound size={16} />}
                  <Text ml={2}>
                    {creating ? 'Creating...' : `Create ${selectedClient.name} key`}
                  </Text>
                </Button>
              </HStack>

              <Text mt="2" fontSize="xs" color="ui.text.tertiary">
                Keys are shown once, hashed server-side, and can be revoked anytime.
              </Text>
            </Box>
          ) : (
            <Box
              py="4"
              borderBottomWidth="1px"
              borderBottomStyle="solid"
              borderBottomColor="ui.border.subtle"
            >
              <HStack gap="2" align="start">
                <Box color="status.success.fg">
                  <ShieldCheck size={16} />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="ui.text.primary">
                    No Attune key needed
                  </Text>
                  <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
                    ChatGPT completes Attune authentication through the OAuth browser flow during
                    Scan Tools.
                  </Text>
                </Box>
              </HStack>
            </Box>
          )}

          <Box pt="4">
            <HStack gap={2} mb="3">
              <Box color="ui.text.secondary">
                <Server size={16} />
              </Box>
              <Text fontWeight="600" color="ui.text.primary">
                Issued MCP keys
              </Text>
            </HStack>
            {loading ? (
              <IntegrationStatusRow
                icon={<InlineSpinner size="xs" />}
                title="Loading issued keys"
              />
            ) : keys.length === 0 ? (
              <Box py="3">
                <Text fontSize="sm" color="ui.text.secondary">
                  No keys yet. Create one to connect an MCP client.
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" gap="0">
                {keys.map(key => (
                  <Flex
                    key={key.id}
                    justify="space-between"
                    align={{ base: 'start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap="3"
                    py="3"
                    borderTopWidth="1px"
                    borderTopStyle="solid"
                    borderColor="ui.border.subtle"
                  >
                    <Box>
                      <HStack gap={2} mb="1" flexWrap="wrap">
                        <Text fontWeight="600" color="ui.text.primary">
                          {key.label}
                        </Text>
                        <CapabilityStatus
                          label={key.status}
                          color={key.status === 'active' ? 'status.success.fg' : 'ui.text.tertiary'}
                        />
                      </HStack>
                      <Flex
                        gap={{ base: '1', md: '4' }}
                        flexWrap="wrap"
                        direction={{ base: 'column', md: 'row' }}
                        fontSize="xs"
                        color="ui.text.tertiary"
                      >
                        <Text title={`Created ${formatDate(key.created_at)}`}>
                          Last used {formatDate(key.last_used_at)}
                        </Text>
                        <Text>Expires {formatDate(key.expires_at)}</Text>
                      </Flex>
                    </Box>
                    <Button
                      size="xs"
                      aria-label={`Revoke ${key.label}`}
                      color="ui.text.tertiary"
                      _hover={{ color: 'status.danger.fg', bg: 'status.danger.bg' }}
                      variant="ghost"
                      borderRadius="8px"
                      onClick={() => handleRevoke(key.id)}
                      disabled={key.status !== 'active' || revokingId === key.id}
                    >
                      {revokingId === key.id ? <InlineSpinner size="xs" /> : <Trash2 size={14} />}
                      <Text ml={2}>{revokingId === key.id ? 'Revoking…' : 'Revoke'}</Text>
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}
          </Box>
        </VStack>
      </IntegrationCard>

      <Dialog.Root
        open={!!createdKey}
        closeOnEscape={false}
        closeOnInteractOutside={false}
        size="xl"
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(6px)" />
          <Dialog.Positioner>
            <Dialog.Content
              bg="ui.surface.card"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="ui.border.default"
              borderRadius="8px"
            >
              <Dialog.Header>
                <Dialog.Title color="ui.text.primary">
                  Your {selectedClient.name} MCP key
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack align="stretch" gap="4">
                  <Dialog.Description fontSize="sm" color="ui.text.secondary">
                    Copy this now. For security, Attune will not show the raw key again after you
                    close this dialog.
                  </Dialog.Description>
                  <Box
                    p="4"
                    bg="ui.surface.selected"
                    borderRadius="8px"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="ui.border.subtle"
                  >
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      color="ui.text.tertiary"
                      mb="2"
                    >
                      API key
                    </Text>
                    <HStack align="start">
                      <Box
                        as="pre"
                        flex="1"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                        fontSize="sm"
                        color="ui.text.primary"
                        m="0"
                      >
                        {createdKey?.value}
                      </Box>
                      <IconButton
                        aria-label="Copy API key"
                        variant="ghost"
                        onClick={() => createdKey && copyText(createdKey.value, 'API key')}
                      >
                        <Copy size={16} />
                      </IconButton>
                    </HStack>
                  </Box>
                  {clientSetup.snippet && clientSetup.snippetTitle ? (
                    <ConfigSnippet
                      title={`Ready ${clientSetup.snippetTitle}`}
                      snippet={clientSetup.snippet}
                      onCopy={() =>
                        copyText(clientSetup.snippet || '', `${selectedClient.name} setup`)
                      }
                    />
                  ) : null}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button borderRadius="8px" onClick={() => setCreatedKey(null)}>
                  I’ve Saved This Key
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}

function ConfigSnippet({
  title,
  snippet,
  onCopy,
}: {
  title: string;
  snippet: string;
  onCopy: () => Promise<void>;
}) {
  return (
    <Box>
      <HStack justify="space-between" mb="2">
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.08em"
          color="ui.text.tertiary"
        >
          {title}
        </Text>
        <Button variant="ghost" size="xs" color="ui.text.secondary" onClick={() => void onCopy()}>
          <Copy size={14} />
          <Text ml={1}>Copy</Text>
        </Button>
      </HStack>
      <Box
        as="pre"
        p="3"
        bg="ui.surface.input"
        borderRadius="8px"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="ui.border.subtle"
        fontSize="xs"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        color="ui.text.primary"
        m="0"
      >
        {snippet}
      </Box>
    </Box>
  );
}
